import {captureException} from '@sentry/browser';
import {Button, DataStatus} from 'platform/components';
import {Center, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {usePostOAuthTokenMutation} from '@dms/api/base';
import {appWorkspaceKey, browserStorageKey, OAUTH_CLIENT_ID} from '@dms/config';
import i18n from '@dms/i18n';
import {
  buildUrl,
  getCommonParentDomain,
  isLocalDevHost,
  clearOAuthFlowCookie,
  readOAuthFlowCookie,
  selectOAuthFlow,
  iamQueryParams,
  dashboardRoutes,
  iamRedirectRoute,
  redirectToLogin,
  workspaceInitializeRoute,
  workspaceRedirectRoute,
  writeOAuthFlowCookie,
} from '@dms/routes';
import {CommonSkeleton, handleApiError} from '@dms/shared';

import {useNavigate, useOnMount} from 'shared/web';

import {getRedirectSearchParams} from './utils/getRedirectSearchParams';

export const WorkspaceRedirect = () => {
  const [postOAuthToken, {isError}] = usePostOAuthTokenMutation();
  const navigate = useNavigate();

  const [areSearchParamsValid, params] = getRedirectSearchParams();

  const [candidates] = useState(readOAuthFlowCookie);
  const state = new URLSearchParams(window.location.search).get(iamQueryParams.STATE);
  const flow = selectOAuthFlow(candidates, state);
  const isStateValid = isNil(state) || !isNil(flow);
  const isRedirectValid = areSearchParamsValid && isStateValid;

  useOnMount(() => {
    clearOAuthFlowCookie();
    if (!areSearchParamsValid || !isStateValid) {
      captureException(new Error('FATAL: Invalid redirect search params'), {extra: {params}});
      return;
    }

    const redirect_uri = buildUrl(workspaceInitializeRoute, appWorkspaceKey, false);

    postOAuthToken({
      grant_type: 'authorization_code',
      code: params.code,
      code_verifier: flow?.verifier,
      redirect_uri,
      workspace: params.workspace,
      client_id: OAUTH_CLIENT_ID,
    })
      .unwrap()
      .then((response) => {
        sessionStorage.setItem(browserStorageKey.ACCESS_TOKEN, response.access_token);
        if (response.refresh_token) {
          localStorage.setItem(browserStorageKey.REFRESH_TOKEN, response.refresh_token);
        }

        navigate(dashboardRoutes.home);
      })
      .catch(handleApiError);
  });

  return (
    <Center height="100vh" width="100%">
      <VStack spacing={4}>
        <DataStatus
          isLoading
          isError={isError || !isRedirectValid}
          errorMessage={
            isRedirectValid
              ? i18n.t('general.errors.invalidLoginAttempt')
              : i18n.t('general.errors.invalidParams')
          }
        />

        <Show when={isError || !isRedirectValid}>
          <Button
            title={i18n.t('page.login.actions.backToLogin')}
            onClick={() =>
              redirectToLogin({
                redirectUrl: buildUrl(iamRedirectRoute, params?.workspace, false),
              })
            }
          />
        </Show>
      </VStack>
    </Center>
  );
};

export const WorkspaceInitialize = () => {
  const [areSearchParamsValid, params] = getRedirectSearchParams();

  useOnMount(() => {
    if (areSearchParamsValid) {
      const redirectUrl = buildUrl(workspaceRedirectRoute, params.workspace);
      const hostname = window.location.hostname;
      const callbackHostname = new URL(redirectUrl).hostname;
      const domain = getCommonParentDomain(hostname, callbackHostname);
      if (isLocalDevHost(hostname) && !domain.includes('.') && hostname !== callbackHostname) {
        clearOAuthFlowCookie();
        window.location.href = redirectUrl;
        return;
      }

      const state = new URLSearchParams(window.location.search).get(iamQueryParams.STATE);
      const flow = selectOAuthFlow(readOAuthFlowCookie(), state);
      if (isNotNil(flow)) {
        writeOAuthFlowCookie(flow, redirectUrl);
      }
      window.location.href = redirectUrl;
    }
  });

  return <CommonSkeleton />;
};
